import React, { useState, useEffect } from "react";
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon, Clock,
  MapPin, Video, Phone, User, X, Eye, Trash2, FileText,
  MapPinned, Loader, CheckCircle
} from "lucide-react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import api from "../../utils/Api";

const CalendarPage = () => {
  const { user } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState("month");
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [filterStatus, setFilterStatus] = useState("all");

  useEffect(() => {
    fetchAppointments();
  }, [currentDate, viewMode]);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showAppointmentModal) {
        setShowAppointmentModal(false);
      }
    };

    if (showAppointmentModal) {
      document.addEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [showAppointmentModal]);

  const fetchAppointments = async () => {
    try {
      const response = await api.get("/appointment", {
        params: { interviewer: user._id },
      });
      setAppointments(response.data.data || []);
    } catch (error) {
      console.error("Error fetching appointments:", error);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    return { daysInMonth, startingDayOfWeek, firstDay, lastDay };
  };

  const getAppointmentsForDate = (date) => {
    if (!appointments || appointments.length === 0) return [];

    return appointments.filter((appointment) => {
      if (!appointment.scheduledAt) return false;

      const appointmentDate = new Date(appointment.scheduledAt);
      const normalizedAppointmentDate = new Date(
        appointmentDate.getFullYear(),
        appointmentDate.getMonth(),
        appointmentDate.getDate()
      );
      const normalizedDate = new Date(
        date.getFullYear(),
        date.getMonth(),
        date.getDate()
      );

      const isSameDate = normalizedAppointmentDate.getTime() === normalizedDate.getTime();
      const matchesFilter = filterStatus === "all" || appointment.status === filterStatus;

      return isSameDate && matchesFilter;
    });
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString("fr-FR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString("fr-FR", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString("fr-FR", {
      month: "long",
      year: "numeric",
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      scheduled: "bg-blue-500",
      completed: "bg-green-500",
      cancelled: "bg-red-500",
      rescheduled: "bg-yellow-500",
    };
    return colors[status] || "bg-gray-500";
  };

  const getStatusBadge = (status) => {
    const config = {
      scheduled: { color: 'bg-blue-50 dark:bg-blue-950/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800', text: 'Planifié' },
      completed: { color: 'bg-green-50 dark:bg-green-950/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800', text: 'Terminé' },
      cancelled: { color: 'bg-red-50 dark:bg-red-950/30 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800', text: 'Annulé' },
      rescheduled: { color: 'bg-yellow-50 dark:bg-yellow-950/30 text-yellow-700 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800', text: 'Reporté' }
    };
    const cfg = config[status] || config.scheduled;
    return <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium border ${cfg.color}`}>{cfg.text}</span>;
  };

  const getInterviewTypeIcon = (appointment) => {
    if (appointment.meetingLink) return <Video className="h-4 w-4" />;
    if (appointment.location) return <MapPinned className="h-4 w-4" />;
    return <Phone className="h-4 w-4" />;
  };

  const handleAppointmentClick = (appointment) => {
    setSelectedAppointment(appointment);
    setShowAppointmentModal(true);
  };

  const handleDeleteAppointment = async (appointmentId) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cet entretien ?")) {
      try {
        await api.delete(`/appointment/${appointmentId}`);
        await fetchAppointments();
        setShowAppointmentModal(false);
      } catch (error) {
        console.error("Error deleting appointment:", error);
        alert("Erreur lors de la suppression");
      }
    }
  };

  const renderMonthView = () => {
    const { daysInMonth, startingDayOfWeek } = getDaysInMonth(currentDate);
    const days = [];
    const weekDays = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];

    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="min-h-24 bg-gray-50 dark:bg-gray-800/50"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const dayAppointments = getAppointmentsForDate(date);
      const isToday = new Date().toDateString() === date.toDateString();
      const isSelected = selectedDate && selectedDate.toDateString() === date.toDateString();

      days.push(
        <div
          key={day}
          onClick={() => setSelectedDate(date)}
          className={`min-h-24 border border-gray-200 dark:border-gray-700 p-2 cursor-pointer transition-all hover:bg-gray-50 dark:hover:bg-gray-700/30 ${
            isSelected
              ? "bg-primary-50 dark:bg-primary-950/30 border-primary-200 dark:border-primary-800"
              : "bg-white dark:bg-gray-800"
          }`}
        >
          <div className={`text-xs font-semibold mb-1 ${
            isToday
              ? "inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-600 text-white"
              : "text-gray-900 dark:text-white"
          }`}>
            {day}
          </div>
          <div className="space-y-1">
            {dayAppointments.slice(0, 2).map((appointment) => (
              <div
                key={appointment._id}
                onClick={(e) => {
                  e.stopPropagation();
                  handleAppointmentClick(appointment);
                }}
                className="text-xs p-1 rounded bg-primary-100 dark:bg-primary-950/30 text-primary-700 dark:text-primary-400 border border-primary-200 dark:border-primary-800 hover:bg-primary-200 dark:hover:bg-primary-900/50 transition-colors truncate"
              >
                <div className="flex items-center gap-1">
                  <div className={`w-1 h-1 rounded-full ${getStatusColor(appointment.status)}`}></div>
                  <span className="font-medium">{formatTime(appointment.scheduledAt)}</span>
                </div>
                <div className="truncate text-[10px]">
                  {appointment.candidate?.first_name} {appointment.candidate?.last_name}
                </div>
              </div>
            ))}
            {dayAppointments.length > 2 && (
              <div className="text-[10px] text-gray-500 dark:text-gray-400 font-medium pl-1">
                +{dayAppointments.length - 2}
              </div>
            )}
          </div>
        </div>
      );
    }

    return (
      <div className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden h-full flex flex-col">
        <div className="grid grid-cols-7 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex-shrink-0">
          {weekDays.map((day) => (
            <div key={day} className="py-2 text-center text-xs font-semibold text-gray-700 dark:text-gray-300">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 flex-1 overflow-hidden">{days}</div>
      </div>
    );
  };

  const stats = {
    scheduled: appointments.filter((a) => a.status === "scheduled").length,
    completed: appointments.filter((a) => a.status === "completed").length,
    rescheduled: appointments.filter((a) => a.status === "rescheduled").length,
    cancelled: appointments.filter((a) => a.status === "cancelled").length
  };

  if (loading) {
    return (
      <div className="h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-10 w-10 text-primary-600 dark:text-primary-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400 font-medium">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col bg-gray-50 dark:bg-gray-950">
      {/* Header - Compact */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800 flex-shrink-0">
        <div className="px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Left: Title + Navigation */}
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-primary-100 dark:bg-primary-950/30 rounded-lg flex items-center justify-center">
                  <CalendarIcon className="h-4 w-4 text-primary-600 dark:text-primary-500" />
                </div>
                <div>
                  <h1 className="text-base font-bold text-gray-900 dark:text-white">Calendrier</h1>
                </div>
              </div>

              {/* Navigation */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => navigateMonth(-1)}
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <ChevronLeft className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </button>
                <button
                  onClick={goToToday}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  Aujourd'hui
                </button>
                <button
                  onClick={() => navigateMonth(1)}
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  <ChevronRight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </button>
                <h2 className="text-sm font-semibold text-gray-900 dark:text-white ml-2 capitalize">
                  {formatMonthYear(currentDate)}
                </h2>
              </div>

              {/* Stats inline */}
              <div className="hidden lg:flex items-center gap-2 text-xs">
                <div className="px-2 py-1 bg-blue-50 dark:bg-blue-950/30 rounded-lg">
                  <span className="text-blue-700 dark:text-blue-400">{stats.scheduled} planifiés</span>
                </div>
                <div className="px-2 py-1 bg-green-50 dark:bg-green-950/30 rounded-lg">
                  <span className="text-green-700 dark:text-green-400">{stats.completed} terminés</span>
                </div>
              </div>
            </div>

            {/* Right: Filter */}
            <div className="flex items-center gap-2">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-3 py-1.5 text-sm bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="all">Tous les statuts</option>
                <option value="scheduled">Planifié</option>
                <option value="completed">Terminé</option>
                <option value="cancelled">Annulé</option>
                <option value="rescheduled">Reporté</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Main Calendar */}
      <div className="h-[calc(100vh-120px)] p-6">
        {renderMonthView()}
      </div>

      {/* Appointment Modal */}
      {showAppointmentModal && selectedAppointment && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
          onClick={() => setShowAppointmentModal(false)}
        >
          <div
            className="relative w-full max-w-2xl bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800">
              <div className="flex items-center gap-3">
                <CalendarIcon className="h-5 w-5 text-primary-600 dark:text-primary-500" />
                <div>
                  <h3 className="font-semibold text-gray-900 dark:text-white">Détails de l'Entretien</h3>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {formatDate(selectedAppointment.scheduledAt)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowAppointmentModal(false)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-4">
              {/* Candidate */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-xl">
                <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-950/30 flex items-center justify-center">
                  <span className="text-lg font-bold text-primary-600 dark:text-primary-500">
                    {selectedAppointment.candidate?.first_name?.charAt(0)}
                    {selectedAppointment.candidate?.last_name?.charAt(0)}
                  </span>
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-white">
                    {selectedAppointment.candidate?.first_name} {selectedAppointment.candidate?.last_name}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {selectedAppointment.candidate?.email}
                  </p>
                </div>
              </div>

              {/* Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Date et Heure
                  </label>
                  <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-white">
                    <Clock className="h-4 w-4 text-gray-400" />
                    <span>{formatTime(selectedAppointment.scheduledAt)}</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Statut
                  </label>
                  {getStatusBadge(selectedAppointment.status)}
                </div>
              </div>

              {selectedAppointment.location && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Lieu</label>
                  <div className="flex items-center gap-2 text-sm text-gray-900 dark:text-white">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <span>{selectedAppointment.location}</span>
                  </div>
                </div>
              )}

              {selectedAppointment.meetingLink && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Lien</label>
                  <div className="flex items-center gap-2">
                    <Video className="h-4 w-4 text-gray-400" />
                    <a
                      href={selectedAppointment.meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary-600 dark:text-primary-400 hover:underline truncate"
                    >
                      {selectedAppointment.meetingLink}
                    </a>
                  </div>
                </div>
              )}

              {selectedAppointment.notes && (
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">Notes</label>
                  <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-sm text-gray-900 dark:text-white">
                    {selectedAppointment.notes}
                  </div>
                </div>
              )}

              {selectedAppointment.context && (
                <div>
                  <button
                    onClick={() => {
                      const type = selectedAppointment.contextModel === "Application" ? "jobs" : "missions";
                      navigate(`/dashboard/applications/${type}/${selectedAppointment.context._id}`);
                    }}
                    className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
                  >
                    <FileText className="h-4 w-4" />
                    Voir la candidature
                  </button>
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={() => handleDeleteAppointment(selectedAppointment._id)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/40 rounded-xl transition-colors flex items-center justify-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Supprimer
                </button>
                <button
                  onClick={() => setShowAppointmentModal(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-xl transition-colors"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CalendarPage;
